// src/components/laboratory/forms/ContractForm.jsx
import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_CONTRACT, UPDATE_CONTRACT } from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { Info, Calendar } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { toast } from 'react-toastify';

const ContractForm = ({ laboratory, contract, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nom: '',
    number: '',
    dateStart: format(new Date(), 'yyyy-MM-dd'),
    dateEnd: format(addMonths(new Date(), 12), 'yyyy-MM-dd'),
    piecesJointes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState(null);

  // Initialize form data if editing an existing contract
  useEffect(() => {
    if (contract) {
      setFormData({
        nom: contract.nom || '',
        number: contract.number || '',
        dateStart: format(new Date(contract.dateStart), 'yyyy-MM-dd'),
        dateEnd: format(new Date(contract.dateEnd), 'yyyy-MM-dd'),
        piecesJointes: contract.piecesJointes || ''});
    } else {
      // Reset form for a new contract
      const contractNumber = `CONT-${format(new Date(), 'yyyy')}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`;
      setFormData({
        nom: '',
        number: contractNumber,
        dateStart: format(new Date(), 'yyyy-MM-dd'),
        dateEnd: format(addMonths(new Date(), 12), 'yyyy-MM-dd'),
        piecesJointes: ''
      });
    }
    setFormErrors({});
    setFile(null);
  }, [contract, isOpen]);

  // Mutations
  const [createContrat] = useMutation(CREATE_CONTRACT, {
    onCompleted: (data) => {
      if (data.createContrat) {
        toast.success('Contrat créé avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la création du contrat');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création du contrat: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updateContract] = useMutation(UPDATE_CONTRACT, {
    onCompleted: (data) => {
      if (data.updateContrat) {
        toast.success('Contrat mis à jour avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour du contrat');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour du contrat: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // File upload handler
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFormData(prev => ({
        ...prev,
        piecesJointes: selectedFile.name
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.number) errors.number = 'Le numéro de contrat est requis';
    if (!formData.dateStart) errors.dateStart = 'La date de début est requise';
    if (!formData.dateEnd) errors.dateEnd = 'La date de fin est requise';

    if (formData.dateStart && formData.dateEnd) {
      const start = new Date(formData.dateStart);
      const end = new Date(formData.dateEnd);

      if (end <= start) {
        errors.dateEnd = 'La date de fin doit être supérieure à la date de début';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Format dates as ISO strings for the backend
      const formattedData = {
        ...formData,
        // Convert date strings to ISO format
        dateStart: new Date(formData.dateStart).toISOString(),
        dateEnd: new Date(formData.dateEnd).toISOString(),
        laboratoryId: laboratory.id
      };

      if (contract) {
        // Update existing contract
        await updateContract({
          variables: {
            updateContratInput: {
              id: contract.id,
              ...formattedData
            }
          }
        });
      } else {
        // Create new contract
        await createContrat({
          variables: {
            createContratInput: formattedData
          }
        });
      }

      // Success is handled in the mutation callbacks
    } catch (error) {
      console.error('Error saving contract:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={contract ? 'Modifier le contrat' : 'Créer un nouveau contrat'}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du contrat
            </label>
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Contrat annuel 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numéro de contrat <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="number"
              value={formData.number}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.number ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="CONT-2025-001"
            />
            {formErrors.number && (
              <p className="mt-1 text-sm text-red-600">{formErrors.number}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateStart"
                value={formData.dateStart}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.dateStart ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.dateStart && (
              <p className="mt-1 text-sm text-red-600">{formErrors.dateStart}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateEnd"
                value={formData.dateEnd}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.dateEnd ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.dateEnd && (
              <p className="mt-1 text-sm text-red-600">{formErrors.dateEnd}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Documents annexes
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload-contract"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-green-700 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                >
                  <span>Ajouter un fichier</span>
                  <input
                    id="file-upload-contract"
                    name="piecesJointes"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">PDF, DOCX jusqu'à 10MB</p>
              {formData.piecesJointes && (
                <p className="text-sm text-green-700">{formData.piecesJointes}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 p-3 bg-blue-50 rounded-md">
          <p className="text-blue-700 text-sm">
            <Info size={16} className="inline mr-1" />
            Ce contrat sera associé au laboratoire <strong>{laboratory?.nom}</strong> et permettra de créer des promotions liées à ce contrat.
          </p>
        </div>
      </div>
    </ModalComponent>
  );
};

export default ContractForm;